import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { X } from 'lucide-react';
import { Check, Loader2, ChevronDown } from 'lucide-react';
import { githubOwnerStore, githubRepoStore, githubBranchStore, clearGithubStores } from '~/lib/stores/integrations';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { openDatabase, clearGithubConnection } from '~/lib/persistence/db';
import { toast } from 'react-toastify';
import { projectId } from '~/lib/persistence';
import { max_commit_length } from '~/utils/constants';
import * as Sentry from '@sentry/remix';
import { ConfirmationDialog } from '~/components/modals/ConfirmationDialog.client';
import { StatusDisplay } from './StatusDisplay';
import { validateRepoOrBranchName } from '~/utils/github';
import axios from 'axios';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';

interface PullRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (targetBranch: string, commitMessage: string) => Promise<string>;
  setIsPRModalOpen: (isOpen: boolean) => void;
  setIsGitRepo: (isGitRepo: boolean) => void;
}

type Status = {
  checkPermissions: 'pending' | 'complete' | 'error';
  createCommit: 'pending' | 'complete' | 'error';
};

interface GitHubBranch {
  name: string;
  commit: {
    sha: string;
    url: string;
  };
  protected: boolean;
}

type BranchOption = 'existing' | 'new';

interface GithubApiResponse {
  type: 'success' | 'failed';
  permission: {
    admin: boolean;
    maintain: boolean;
    push: boolean;
    triage: boolean;
    pull: boolean;
  };
  error?: string;
}

// Add this interface for the error response
interface GitHubErrorResponse {
  status: string;
  message: string;
}

interface GitHubCommitResponse {
  sha: string;
  url: string;
  html_url: string;
  commit: {
    message: string;
    author: {
      name: string;
      email: string;
      date: string;
    };
  };
}

export const PullRequestModal: React.FC<PullRequestModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  setIsPRModalOpen,
  setIsGitRepo,
}) => {
  const { t } = useTranslation();
  const { trackGithubEvent } = usePosthogCommonEvents();
  console.log('>>>[PullRequestModal] isOpen: ', isOpen);

  const [currentStep, setCurrentStep] = useState<string>('');
  const [status, setStatus] = useState<Status>({
    checkPermissions: 'pending',
    createCommit: 'pending',
  });
  const [error, setError] = useState<string | null>(null);
  const [prUrl, setPrUrl] = useState<string>('');
  const [commitUrl, setCommitUrl] = useState<string>('');
  const [commitSha, setCommitSha] = useState<string>('');
  const [branches, setBranches] = useState<string[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [branchOption, setBranchOption] = useState<BranchOption>('existing');
  const [newBranchName, setNewBranchName] = useState<string>('');
  const [isOptionDropdownOpen, setIsOptionDropdownOpen] = useState(false);
  const [isCheckingBranch, setIsCheckingBranch] = useState(false);
  const [branchAvailability, setBranchAvailability] = useState<'available' | 'taken' | 'invalid' | null>(null);
  const [commitMessage, setCommitMessage] = useState<string>('Commit from Dualite: ');
  const [isDisconnectConfirmOpen, setIsDisconnectConfirmOpen] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const fetchUserInfo = async (token: string) => {
    try {
      const response = await axios.get('https://api.github.com/user', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.data;

      const userName = data.login;
      console.log('>>>[fetchUserInfo] userName: ', userName);
      return userName;
    } catch (error) {
      console.error('Error fetching user info:', error);
      return '';
    }
  };
  useEffect(() => {
    const fetchBranches = async () => {
      setIsLoading(true);
      setError('');

      const githubRepo = githubRepoStore.get();
      const owner = githubOwnerStore.get();
      const branch = githubBranchStore.get();
      const token = selectionStore.get().githubAccessToken;

      console.log('>>>[fetchBranches] githubRepo: ', githubRepo, 'branch: ', branch, 'owner: ', owner);

      if (!githubRepo || !owner || !branch) {
        setError('Invalid repository info');
        setIsLoading(false);
        return;
      }

      const repoInfo = {
        owner,
        repo: githubRepo,
        branch,
      };
      const userNameData = await fetchUserInfo(token);
      console.log('>>>[fetchBranches] userName: ', userNameData);

      if (userNameData) {
        setUserName(userNameData);
      } else {
        setUserName('');
        setError('Failed to fetch user info');
        setIsLoading(false);
        localStorage.removeItem('githubAccessToken');
        return;
      }

      console.log('>>>[fetchBranches] repoInfo: ', repoInfo);

      try {
        await Sentry.startSpan({ name: 'fetchBranches' }, async () => {
          const response = await fetch(`https://api.github.com/repos/${repoInfo.owner}/${repoInfo.repo}/branches`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          // First check if response is not ok
          if (!response.ok) {
            const errorData = (await response.json()) as GitHubErrorResponse;

            if (response.status === 404) {
              throw new Error('This project does not exist on GitHub');
            }

            throw new Error(errorData.message || 'Failed to fetch branches');
          }

          const data = (await response.json()) as GitHubBranch[];
          console.log('>>>[fetchBranches] data: ', data, typeof data, Array.isArray(data));

          if (!Array.isArray(data)) {
            throw new Error('Invalid response format');
          }

          // Extract branch names from the response
          const branchNames = data.map((branch) => branch.name);
          console.log('>>>[fetchBranches] branches: ', branchNames);

          setBranches(branchNames);
          setSelectedBranch(repoInfo.branch);
        });
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            function: 'Error in fetchBranches',
            repoInfo,
          },
        });
        console.error('Error fetching branches:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch branches');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchBranches();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Reset states when modal opens
      setCurrentStep('');
      setStatus({
        checkPermissions: 'pending',
        createCommit: 'pending',
      });
      setError('');
      setPrUrl('');

      setIsDisconnectConfirmOpen(false);
    }
  }, [isOpen]);

  const resetModal = () => {
    // Reset all states to initial values
    setCurrentStep('');
    setStatus({
      checkPermissions: 'pending',
      createCommit: 'pending',
    });
    setError('');
    setPrUrl('');
    setNewBranchName('');
    setCommitMessage('Commit from Dualite: ');
    setBranchAvailability(null);
    setIsDropdownOpen(false);
    setIsOptionDropdownOpen(false);
    setIsCheckingBranch(false);
    setIsLoading(false);

    // Trigger branch fetch to reset selectedBranch
    const branch = githubBranchStore.get();

    if (branch) {
      setSelectedBranch(branch);
    }
  };

  const checkBranchName = async (branchName: string) => {
    branchName = branchName.trim();

    // Validate branch name format: only allow letters, numbers, hyphens, and underscores
    if (!validateRepoOrBranchName(branchName)) {
      setBranchAvailability('invalid');
      return false;
    }

    try {
      setIsCheckingBranch(true);

      return await Sentry.startSpan({ name: 'pullRequestModal.checkBranchName' }, async () => {
        const githubRepo = githubRepoStore.get();
        const owner = githubOwnerStore.get();

        const response = await fetch(`https://api.github.com/repos/${owner}/${githubRepo}/branches/${branchName}`);

        if (response.status === 404) {
          setBranchAvailability('available');
          return true;
        } else {
          setBranchAvailability('taken');
          return false;
        }
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          function: 'Error in checkBranchName',
          branchName,
        },
      });
      console.error('Error checking branch name:', error);
      setBranchAvailability('invalid');
      return false;
    } finally {
      setIsCheckingBranch(false);
    }
  };

  const checkPermissions = async () => {
    const githubRepo = githubRepoStore.get();
    const owner = githubOwnerStore.get();
    const token = selectionStore.get().githubAccessToken;
    console.log('>>>[checkPermissions] ', 'githubRepo: ', githubRepo, 'owner: ', owner);

    if (!token) {
      throw new Error('GitHub token not found. Please reconnect your GitHub account.');
    }

    if (newBranchName) {
      return true;
    }

    try {
      const response = await fetch('/api/github', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'checkPermissions',
          repo: `${owner}/${githubRepo}`,
        }),
      });

      console.log('>>>[checkPermissions] response: ', response);

      if (!response.ok) {
        throw new Error('Permission check failed');
      }

      const data = (await response.json()) as GithubApiResponse;

      if (data.type === 'failed') {
        throw new Error(data.error || 'Failed to verify repository permissions');
      }

      console.log('>>>[checkPermissions] permission: ', data.permission);

      if (!data.permission.pull || !data.permission.push) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Permission check error:', error);
      return false;
    }
  };

  const handleConfirm = async () => {
    console.log('>>>[handleConfirm] Starting confirmation flow', {
      branchOption,
      selectedBranch,
      newBranchName,
      branchAvailability,
    });

    if (branchOption === 'existing' && !selectedBranch) {
      setError('Please select a target branch');
      return;
    }

    if (branchOption === 'new') {
      console.log('>>>[handleConfirm] Checking new branch availability', branchAvailability);

      if (branchAvailability !== 'available' && branchAvailability === 'invalid') {
        setError('Please check the branch name.');
        return;
      } else if (!branchAvailability) {
        const isAvailable = await checkBranchName(newBranchName);

        if (!isAvailable) {
          setError('Branch name is not available. Please choose a different name.');
          return;
        }
      }
    }

    try {
      setCurrentStep('checkPermissions');
      setStatus((prev) => ({ ...prev, checkPermissions: 'pending' }));

      const hasPermissions = await checkPermissions();
      console.log('>>>[handleConfirm] Permission check result:', hasPermissions);

      if (!hasPermissions) {
        console.warn('>>>[handleConfirm] Insufficient permissions');
        setError('Insufficient permissions to create commit');
        setStatus((prev) => ({ ...prev, checkPermissions: 'error' }));
        return;
      }

      setStatus((prev) => ({ ...prev, checkPermissions: 'complete' }));

      setCurrentStep('createCommit');
      setStatus((prev) => ({ ...prev, createCommit: 'pending' }));

      const targetBranch = branchOption === 'new' ? newBranchName : selectedBranch;
      console.log('>>>[handleConfirm] Creating commit with:', {
        targetBranch,
        commitMessage,
      });

      const statusCheck = await onConfirm(targetBranch, commitMessage);
      console.log('>>>[handleConfirm] Commit creation result:', statusCheck);

      if (statusCheck === 'success') {
        // Get the latest commit SHA for the branch
        const owner = githubOwnerStore.get();
        const repo = githubRepoStore.get();
        const branch = branchOption === 'new' ? newBranchName : selectedBranch;

        try {
          const response = await fetch(`https://api.github.com/repos/${owner}/${repo}/commits/${branch}`, {
            headers: {
              Authorization: `Bearer ${selectionStore.get().githubAccessToken}`,
            },
          });

          if (response.ok) {
            const data = (await response.json()) as GitHubCommitResponse;
            const sha = data.sha;
            setCommitSha(sha);
            setCommitUrl(`https://github.com/${owner}/${repo}/commit/${sha}`);
          }
        } catch (error) {
          console.error('Error fetching commit SHA:', error);

          // Fallback to branch commits page if we can't get the specific SHA
          setCommitUrl(`https://github.com/${owner}/${repo}/commits/${branch}`);
        }
      }

      setStatus((prev) => ({ ...prev, createCommit: statusCheck === 'success' ? 'complete' : 'error' }));

      // Track GitHub push/commit result
      if (statusCheck === 'success') {
        const targetBranch = branchOption === 'new' ? newBranchName : selectedBranch;

        try {
          trackGithubEvent('push_completed', {
            target_branch: targetBranch,
            repo: githubRepoStore.get(),
            owner: githubOwnerStore.get(),
            commit_sha: commitSha,
          });
        } catch (error) {
          console.error('[PostHog] Failed to track github_push_completed:', error);
        }
      }
    } catch (err: any) {
      console.error('>>>[handleConfirm] Error:', err);

      let errorMessage = err.details || err.message || 'Failed to create commit';

      if (err.type === 'MERGE_CONFLICT') {
        errorMessage = 'There are conflicts with the existing branch. Please pull the latest changes and try again.';
      } else if (err.type === 'PERMISSION_ERROR') {
        errorMessage = 'Unable to access repository. Please check your permissions.';
      }

      setCurrentStep('error');
      setError(errorMessage);
      setStatus((prev) => ({ ...prev, createCommit: 'error' }));
    }
  };

  const handleDisconnectGithub = async () => {
    setIsDisconnectConfirmOpen(true);
  };

  const confirmDisconnectGithub = async () => {
    try {
      console.log('[db] openDatabase called from confirmDisconnectGithub');

      const db = await openDatabase();
      const pId = projectId.get();

      if (!db || !pId) {
        throw new Error('Failed to open database');
      }

      // cloudRevert (burst)
      // await deleteProjectConnection(pId, 'github');
      // await updateIsGitStatus(pId, false);

      await clearGithubConnection(db, projectId.get()!);
      db.close();
      clearGithubStores();
      setError('');
      setIsPRModalOpen(false);
      setIsGitRepo(false);
      toast.success('GitHub repository disconnected successfully', { autoClose: 1000 });
      onClose();
    } catch (err) {
      console.error('Failed to disconnect GitHub:', err);
      toast.error('Failed to disconnect GitHub repository', { autoClose: 3000 });
    }
  };

  if (!isOpen) {
    return null;
  }

  const StatusIcon = ({ status }: { status: 'pending' | 'complete' | 'error' }) => {
    console.log('>>>[StatusIcon] status: ', status);

    if (status === 'complete') {
      return <Check size={16} className="text-green-500" />;
    }

    if (status === 'pending') {
      return <div className="w-4 h-4 rounded-full border-2 border-gray-400" />;
    }

    if (status === 'error') {
      return <X size={16} className="text-red-500" />;
    }

    return null;
  };

  // Add this function to check if form is valid
  const isFormValid = () => {
    // Check for any errors
    if (error) {
      return false;
    }

    // Check if loading
    if (isLoading) {
      return false;
    }

    // For existing branch option
    if (branchOption === 'existing' && !selectedBranch) {
      return false;
    }

    // For new branch option
    if (branchOption === 'new') {
      if (!newBranchName.trim() || branchAvailability === 'taken' || branchAvailability === 'invalid') {
        return false;
      }
    }

    return true;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 z-50" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4 max-w-[480px]">
        {/* Updated background and border to match GithubAuthModal */}
        <div className="bg-dualite-background-tertiary text-dualite-text-primary rounded-lg border border-dualite-border-primary relative w-full max-w-[480px] overflow-hidden shadow-2xl">
          <div className="flex justify-between items-center p-6 pb-2">
            <h2 className="text-md font-medium m-0">{t('modals.pullRequest.title')}</h2>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="p-1 text-dualite-text-modal-caption bg-transparent border-none rounded-full hover:text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="px-6 py-2 flex flex-row gap-4 justify-between items-center">
            <div className="flex flex-col gap-2 min-w-0 overflow-hidden">
              <div className="flex items-center gap-2 text-sm text-dualite-text-secondary">
                <span className="i-ph-git-branch text-[16px] shrink-0" />
                <span
                  className="font-medium text-dualite-text-primary truncate"
                  title={`${githubOwnerStore.get()}/${githubRepoStore.get()}`}
                >
                  {githubOwnerStore.get()}/{githubRepoStore.get()}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-dualite-text-secondary">
                <span className="i-ph-user text-[16px] shrink-0" />
                <span className="shrink-0">{t('modals.pullRequest.connectedUser')} </span>
                <span className="font-medium text-dualite-text-primary truncate" title={userName}>
                  {userName}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-dualite-text-secondary">
                <span className="i-ph-git-fork text-[16px] shrink-0" />
                <span className="shrink-0">{t('modals.pullRequest.sourceBranch')} </span>
                <span className="font-medium text-dualite-text-primary truncate" title={githubBranchStore.get()}>
                  {githubBranchStore.get()}
                </span>
              </div>
            </div>
            <button
              onClick={handleDisconnectGithub}
              className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors rounded-md flex items-center gap-1.5 border border-red-500/20 shrink-0"
              title={t('modals.pullRequest.disconnectRepo', 'Unlink this repository from the project')}
            >
              <span className="i-ph-link-break-bold text-[12px]" />
              Unlink Repository
            </button>
          </div>

          <div className="p-6 pt-4 space-y-5">
            {currentStep ? (
              <div className="space-y-4 py-2">
                <div className="flex items-center gap-3">
                  {currentStep === 'checkPermissions' && status.checkPermissions !== 'complete' && !error ? (
                    <Loader2 size={16} className="animate-spin text-blue-500" />
                  ) : (
                    <StatusIcon status={status.checkPermissions} />
                  )}
                  <span
                    className={`text-sm ${status.checkPermissions === 'error' ? 'text-red-500' : status.checkPermissions === 'complete' ? 'text-green-500' : 'text-dualite-text-primary'}`}
                  >
                    {t('modals.pullRequest.checkPermissions')}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {currentStep === 'createCommit' && status.createCommit !== 'complete' && !error ? (
                    <Loader2 size={16} className="animate-spin text-blue-500" />
                  ) : (
                    <StatusIcon status={status.createCommit} />
                  )}
                  <span
                    className={`text-sm ${status.createCommit === 'complete' ? 'text-green-500' : 'text-dualite-text-primary'}`}
                  >
                    Create Commit
                  </span>
                </div>

                {status.createCommit === 'complete' && (
                  <>
                    <StatusDisplay
                      status="success"
                      title={t('modals.pullRequest.commitCreatedSuccess')}
                      message={
                        <div className="space-y-2">
                          <p>
                            {t('modals.pullRequest.commitPushed', {
                              branch: branchOption === 'existing' ? selectedBranch : newBranchName,
                            })}
                          </p>
                          {commitUrl && (
                            <a
                              href={commitUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-1"
                            >
                              <span className="i-ph-git-commit text-[14px]" />
                              {commitSha
                                ? t('modals.pullRequest.viewCommit', { sha: commitSha.substring(0, 7) })
                                : t('modals.pullRequest.viewCommitGeneric')}
                            </a>
                          )}
                        </div>
                      }
                    />
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        className="px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg hover:border-dualite-border-secondary hover:bg-dualite-background-button-hover transition-colors text-sm font-medium"
                      >
                        Close
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          resetModal();
                        }}
                        className="px-6 py-2.5 bg-dualite-background-cta hover:bg-dualite-background-cta-hover text-dualite-text-cta rounded-lg text-sm font-medium hover:brightness-94 transition-all"
                      >
                        {t('modals.pullRequest.createAnother')}
                      </button>
                    </div>
                  </>
                )}

                {error && (
                  <>
                    <StatusDisplay status="error" message={error} />
                    {error.includes('Insufficient permissions') && (
                      <div className="w-full mt-2 px-4 py-2 text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-medium">
                        Disconnect the repository & create a new one
                      </div>
                    )}
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        className="px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg hover:border-dualite-border-secondary hover:bg-dualite-background-button-hover transition-colors text-sm font-medium"
                      >
                        Close
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setError('');
                          resetModal();
                        }}
                        className="px-6 py-2.5 bg-dualite-background-cta hover:bg-dualite-background-cta-hover text-dualite-text-cta text-sm font-medium hover:brightness-94 rounded-lg transition-all"
                      >
                        {t('modals.pullRequest.tryAgain')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="relative mb-6">
                    <label className="block text-dualite-text-secondary text-sm mb-2">
                      {t('modals.pullRequest.chooseOption')}
                    </label>
                    <button
                      onClick={() => setIsOptionDropdownOpen(!isOptionDropdownOpen)}
                      className="w-full px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg hover:border-dualite-border-secondary transition-colors text-sm font-medium flex justify-between items-center"
                    >
                      <span>
                        {branchOption === 'existing'
                          ? t('modals.pullRequest.pushExisting')
                          : t('modals.pullRequest.createNew')}
                      </span>
                      <ChevronDown size={16} />
                    </button>

                    {isOptionDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-dualite-background-secondary border border-dualite-border-primary rounded-lg overflow-hidden z-50 shadow-lg">
                        <button
                          onClick={() => {
                            setBranchOption('existing');
                            setIsOptionDropdownOpen(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors"
                        >
                          {t('modals.pullRequest.pushExisting')}
                        </button>
                        <button
                          onClick={() => {
                            setBranchOption('new');
                            setIsOptionDropdownOpen(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors"
                        >
                          {t('modals.pullRequest.createNew')}
                        </button>
                      </div>
                    )}
                  </div>

                  {branchOption === 'existing' ? (
                    <div className="space-y-2">
                      <label className="block text-dualite-text-secondary text-sm">
                        {t('modals.pullRequest.selectTarget')}
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="w-full px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg hover:border-dualite-border-secondary transition-colors text-sm font-medium flex justify-between items-center"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span className="flex items-center gap-2">
                              <Loader2 size={16} className="animate-spin" />
                              {t('modals.pullRequest.loadingBranches')}
                            </span>
                          ) : (
                            <>
                              <span>{selectedBranch || t('modals.pullRequest.selectBranch')}</span>
                              <ChevronDown size={16} />
                            </>
                          )}
                        </button>

                        {isDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-dualite-background-secondary border border-dualite-border-primary rounded-lg max-h-48 overflow-y-auto z-50 shadow-lg">
                            {branches.map((branch) => (
                              <button
                                key={branch}
                                onClick={() => {
                                  setSelectedBranch(branch);
                                  setIsDropdownOpen(false);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors"
                              >
                                {branch}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="block text-dualite-text-secondary text-sm">
                        {t('modals.pullRequest.newBranchName')}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder={t('modals.pullRequest.enterBranchName')}
                          value={newBranchName}
                          onChange={(e) => {
                            const filteredValue = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 40);
                            setNewBranchName(filteredValue);
                            setBranchAvailability(null);
                            setError(null);
                          }}
                          className={`flex-1 px-4 py-2.5 text-dualite-text-primary bg-transparent border rounded-lg focus:outline-none focus:border-blue-500 text-sm ${
                            branchAvailability === 'available'
                              ? 'border-green-500'
                              : branchAvailability === 'taken' || branchAvailability === 'invalid'
                                ? 'border-red-500'
                                : 'border-dualite-border-primary focus:border-dualite-border-secondary'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => checkBranchName(newBranchName)}
                          disabled={isCheckingBranch || !newBranchName.trim()}
                          className="px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg hover:border-dualite-border-secondary hover:bg-dualite-background-button-hover transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          {isCheckingBranch ? (
                            <span className="flex items-center gap-2">
                              <Loader2 size={16} className="animate-spin" />
                              {t('modals.pullRequest.checking')}
                            </span>
                          ) : (
                            'Check Availability'
                          )}
                        </button>
                      </div>
                      {branchAvailability && (
                        <p
                          className={`text-sm ${
                            branchAvailability === 'available'
                              ? 'text-green-500'
                              : branchAvailability === 'taken' || branchAvailability === 'invalid'
                                ? 'text-red-500'
                                : ''
                          }`}
                        >
                          {branchAvailability === 'available'
                            ? t('modals.pullRequest.branchAvailable')
                            : branchAvailability === 'taken'
                              ? t('modals.pullRequest.branchTaken')
                              : branchAvailability === 'invalid'
                                ? t('modals.pullRequest.branchInvalid')
                                : ''}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-dualite-text-secondary text-sm">
                    {t('modals.pullRequest.commitMessage')}
                  </label>
                  <textarea
                    value={commitMessage}
                    onChange={(e) => {
                      // Limit input to max_commit_length characters
                      if (e.target.value.length <= max_commit_length) {
                        setCommitMessage(e.target.value);
                        setError(''); // Clear error when message changes
                      }
                    }}
                    placeholder={t('modals.pullRequest.enterCommitMessage')}
                    className="w-full px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg focus:outline-none focus:border-dualite-border-secondary hover:border-dualite-border-secondary transition-colors text-sm min-h-[80px] resize-none"
                    maxLength={max_commit_length}
                  />
                  <div className="flex justify-end">
                    <span
                      className={`text-xs ${commitMessage.length >= max_commit_length * 0.9 ? 'text-amber-500' : 'text-dualite-text-secondary'}`}
                    >
                      {commitMessage.length}/{max_commit_length}
                    </span>
                  </div>
                </div>

                {error && <StatusDisplay status="error" message={error} />}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                    }}
                    className="px-4 py-2.5 text-dualite-text-primary bg-transparent border border-dualite-border-primary rounded-lg hover:border-dualite-border-secondary hover:bg-dualite-background-button-hover transition-colors text-sm font-medium"
                  >
                    {t('modals.pullRequest.cancel')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirm();
                    }}
                    className={`px-6 py-2.5 bg-dualite-background-cta hover:bg-dualite-background-cta-hover text-dualite-text-cta rounded-lg text-sm font-medium transition-all ${
                      !isFormValid() ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-94'
                    }`}
                    disabled={!isFormValid()}
                  >
                    Create Commit
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={isDisconnectConfirmOpen}
        onClose={() => setIsDisconnectConfirmOpen(false)}
        onConfirm={confirmDisconnectGithub}
        title={t('modals.pullRequest.disconnectRepo')}
        description={t('modals.pullRequest.disconnectConfirm')}
        confirmButtonText="Disconnect"
        confirmButtonType="danger"
      />
    </Modal>
  );
};
